import { useState } from "react"

import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import type { WebSearchResult } from "../../types/activity-message-types"

import { ChevronDown, ChevronUp, Globe } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { logger } from "@/lib/logger"

type WebSearchResultsProps = {
  activity: WebSearchResult
  demoMode?: boolean
}

const MAX_INITIAL_RESULTS = 4

export function WebSearchResults({ activity, demoMode = false }: WebSearchResultsProps) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  if (activity.results.length === 0) {
    return null
  }

  const visibleResults = isExpanded
    ? activity.results
    : activity.results.slice(0, MAX_INITIAL_RESULTS)

  const hasMoreResults = activity.results.length > MAX_INITIAL_RESULTS

  return (
    <section aria-label="Web search results" className="flex min-w-0 flex-col gap-3">
      <div className="grid min-w-0 gap-2" role="list">
        {visibleResults.map((result, index) => (
          <WebSearchResultItem key={result.url} demoMode={demoMode} index={index} result={result} />
        ))}
      </div>

      {hasMoreResults && (
        <div className="flex justify-center pt-1">
          <Button
            aria-controls="web-search-results-list"
            aria-expanded={isExpanded}
            className="text-muted-foreground focus:ring-ring h-8 px-4 text-xs font-medium focus:ring-offset-1"
            size="sm"
            variant="ghost"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="mr-1.5 size-3.5" strokeWidth={2} />
                {t("arrowbutton.show_less")}
              </>
            ) : (
              <>
                <ChevronDown className="mr-1.5 size-3.5" strokeWidth={2} />
                {t("arrowbutton.show_more")} ({activity.results.length - MAX_INITIAL_RESULTS})
              </>
            )}
          </Button>
        </div>
      )}
    </section>
  )
}

type WebSearchResultItemProps = {
  result: {
    title: string
    url: string
  }
  index?: number
  demoMode?: boolean
}

function WebSearchResultItem({ result, index = 0, demoMode = false }: WebSearchResultItemProps) {
  let baseUrl = ""
  try {
    baseUrl = new URL(result.url).hostname
  } catch (error) {
    logger.error(`Invalid URL: ${result.url}`, { error })
  }
  const iconSrc = `https://icons.duckduckgo.com/ip3/${baseUrl}.ico`

  const content = (
    <article
      className="border-border/50 animate-in fade-in-0 slide-in-from-bottom-1 hover:bg-muted/50 relative flex items-center gap-3 rounded-lg border p-3 transition-[background-color,box-shadow,scale] duration-200 group-hover/result:scale-[1.01] hover:shadow-sm"
      role="listitem"
    >
      <div className="ring-border/20 group-hover/result:ring-border/40 flex size-7 shrink-0 items-center justify-center rounded-md ring-1 transition-shadow">
        <img
          alt={`${baseUrl} favicon`}
          className="size-4 rounded-sm"
          src={iconSrc}
          onError={(e) => {
            e.currentTarget.style.display = "none"
            e.currentTarget.nextElementSibling?.classList.remove("hidden")
          }}
        />
        <Globe className="text-muted-foreground hidden size-4" strokeWidth={1.5} />
      </div>

      <div className="min-w-0 flex-1 space-y-1 overflow-hidden">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-foreground/90 group-hover/result:text-foreground max-w-[100px] shrink-0 truncate text-sm font-medium">
            {baseUrl}
          </span>
          <div className="bg-muted-foreground/40 h-1 w-1 shrink-0 rounded-full" />
          <span className="text-muted-foreground/80 min-w-0 flex-1 truncate text-xs">
            {result.title}
          </span>
        </div>
        <div className="text-muted-foreground/60 w-full truncate text-xs">{result.url}</div>
      </div>

      {!demoMode && (
        <div className="flex shrink-0 opacity-0 transition-opacity group-hover/result:opacity-100">
          <Globe className="text-muted-foreground size-4" strokeWidth={1.5} />
        </div>
      )}
    </article>
  )

  if (demoMode) {
    return (
      <div
        className="group/result block min-w-0 rounded-lg"
        style={{ animationDelay: `${index * 50}ms` }}
      >
        {content}
      </div>
    )
  }

  return (
    <Link
      aria-label={`Visit ${result.title} on ${baseUrl}`}
      className="group/result block min-w-0 rounded-lg"
      referrerPolicy="no-referrer"
      style={{ animationDelay: `${index * 50}ms` }}
      target="_blank"
      to={result.url}
    >
      {content}
    </Link>
  )
}
